import { AfterViewChecked, Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, from } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs/operators';

import {
  ProjektDetail,
  ProjektRequest,
  ProjektZugriffBenutzer
} from '../../../Models/projekt.model';

import {
  AnlagenteilFortschritt,
  InbetriebnahmeAnlagenteile,
  MessstellenListeSummary
} from '../../../Models/messstelle.model';

import { IbnProtokoll } from '../../../Models/ibn-protokoll.model';
import { Customer } from '../../../Models/customer.model';
import { Equipment } from '../../../Models/equipment.model';

import { ProjektService } from '../../../Services/projekt.service';
import { InbetriebnahmeService } from '../../../Services/inbetriebnahme.service';
import { CustomerService } from '../../../Services/customer.service';
import { EquipmentService } from '../../../Services/equipment.service';
import { IbnProtokollService } from '../../../Services/ibn-protokoll.service';
import { AuthService } from '../../../api-authorization/authorize.service';

import {
  buildIbnProtokollPdfBytes,
  downloadIbnProtokollPdf,
  downloadIbnProtokollSammelPdf
} from '../../Wartung/shared/ibn-protokoll-pdf.util';

import {
  ZipEintrag,
  downloadZip,
  sichererDateiName
} from '../../Wartung/shared/zip.util';

import {
  IbnProtokollAuswahl,
  IbnProtokollDialogComponent
} from './ibn-protokoll-dialog/ibn-protokoll-dialog.component';

import {
  IbnProtokollMessstellenAuswahl,
  MessstelleAuswahlOption,
  auswahlAnzeigeName
} from './ibn-protokoll-dialog/ibn-protokoll-auswahl.model';

import {
  IbnProtokollMessstelleDialogComponent
} from './ibn-protokoll-dialog/messstelle/ibn-protokoll-messstelle-dialog.component';

import {
  IbnProtokollMessstellenDialogComponent
} from './ibn-protokoll-dialog/messstellen/ibn-protokoll-messstellen-dialog.component';

import { IbnWizardService } from '../../shared/ibn-wizard/ibn-wizard.service';

// SPS: Listentypen, SPS-Listen und SPS-Fortschritt über die echte API.
import {
  ListenTypInfo,
  ProjektListenTyp,
  SpsAnlagenteilFortschritt,
  SpsAnlagenteilUebersicht,
  SpsFortschritt,
  SpsImportListe,
  istSpsListenTyp,
  leereSpsAnlagenteilUebersicht,
  leererSpsFortschritt,
  listenTypInfo
} from '../../../Models/Sps';
import { SpsImportApiService } from '../../../Services/sps-import-api.service';
import { SpsListenLoeschenService } from '../../../Services/sps-listen-loeschen.service';
import {
  ListentypAuswahlDialogComponent
} from '../../Wartung/inbetriebnahme/sps/listentyp-auswahl-dialog/listentyp-auswahl-dialog.component';

@Component({
  selector: 'app-projekt-detail',
  templateUrl: './projekt-detail.component.html',
  styleUrls: ['./projekt-detail.component.css']
})
export class ProjektDetailComponent implements OnInit, AfterViewChecked {

  projekt: ProjektDetail | null = null;

  isLoading = true;
  loadError = '';

  ohneVerbindung = false;


  // ========================================================
  // PROJEKT BEARBEITEN
  // ========================================================

  editOpen = false;
  saving = false;
  formError = '';

  form: ProjektRequest = {
    name: '',
    projektNummer: '',
    customerId: null,
    equipmentId: null,
    userIds: []
  };

  kunden: Customer[] = [];
  anlagen: Equipment[] = [];
  loadingAnlagen = false;

  // ========================================================
  // PROJEKTZUGRIFF
  // ========================================================

  zugriffOpen = false;

  zugriffsBenutzer: ProjektZugriffBenutzer[] = [];

  ausgewaehlteUserIds: string[] = [];

  loadingZugriff = false;

  savingZugriff = false;

  zugriffError = '';


  // ========================================================
  // IBN LÖSCHEN
  // ========================================================

  /**
   * IDs der Inbetriebnahmen, die gerade gelöscht werden.
   * Verhindert Doppel-Klicks.
   */
  private loeschendeInbetriebnahmen = new Set<number>();


  // ========================================================
  // IBN UMBENENNEN (INLINE) UND DUPLIZIEREN
  //
  // Beides läuft über das Drei-Punkte-Menü der Listenzeile. Umbenannt wird
  // direkt im Titel – kein Dialog, der Name wird an seiner Stelle zum
  // Eingabefeld. Nach dem Duplizieren steht die Kopie unter dem Original
  // und ihr Titel ist sofort im Bearbeitungsmodus.
  // ========================================================

  /** Liste, deren Name gerade bearbeitet wird – `null` = keine. */
  listeInBearbeitungId: number | null = null;

  listeNameEntwurf = '';

  listeNameSpeichert = false;

  listeNameFehler = '';

  /** IDs der Inbetriebnahmen, die gerade kopiert werden. */
  private duplizierendeInbetriebnahmen = new Set<number>();

  /**
   * Das Eingabefeld soll den Fokus bekommen, sobald es da ist. Es entsteht per
   * *ngIf erst nach dem nächsten Rendern – deshalb merken und in
   * ngAfterViewChecked nachholen.
   */
  private listeNameFokusAusstehend = false;

  @ViewChildren('listeNameEingabe')
  private listeNameEingaben?: QueryList<ElementRef<HTMLInputElement>>;


  ngAfterViewChecked(): void {

    if (!this.listeNameFokusAusstehend) {
      return;
    }

    const eingabe = this.listeNameEingaben?.first?.nativeElement;

    if (eingabe) {
      this.listeNameFokusAusstehend = false;
      eingabe.focus();
      eingabe.select();
    }
  }


  istListeInBearbeitung(
    id: number
  ): boolean {

    return this.listeInBearbeitungId === id;
  }


  istListeAmDuplizieren(
    id: number
  ): boolean {

    return this.duplizierendeInbetriebnahmen.has(id);
  }


  /** Eine Zeile ist beschäftigt, solange sie gelöscht oder kopiert wird. */
  istListeBeschaeftigt(
    id: number
  ): boolean {

    return this.istInbetriebnahmeAmLoeschen(id)
      || this.istListeAmDuplizieren(id);
  }


  /**
   * Klicks im Aktionsbereich dürfen die Zeile nicht öffnen – die ganze Zeile
   * ist klickbar. Der Menüknopf und das Menü selbst stoppen deshalb hier.
   */
  listeAktionenKlick(
    event: Event
  ): void {

    event.stopPropagation();
  }


  listeBearbeiten(
    inb: MessstellenListeSummary
  ): void {

    if (!this.canEdit || this.istListeBeschaeftigt(inb.id)) {
      return;
    }

    this.listeInBearbeitungId = inb.id;
    this.listeNameEntwurf = inb.name;
    this.listeNameFehler = '';
    this.listeNameSpeichert = false;
    this.listeNameFokusAusstehend = true;
  }


  listeBearbeitenAbbrechen(): void {

    if (this.listeNameSpeichert) {
      return;
    }

    this.listeInBearbeitungId = null;
    this.listeNameEntwurf = '';
    this.listeNameFehler = '';
  }


  /** Tasten im Eingabefeld: Enter speichert, Escape bricht ab. */
  listeNameTaste(
    event: KeyboardEvent
  ): void {

    if (event.key === 'Enter') {
      event.preventDefault();
      this.listeBearbeitenSpeichern();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.listeBearbeitenAbbrechen();
    }
  }


  listeBearbeitenSpeichern(): void {

    const id = this.listeInBearbeitungId;
    const inb = this.projekt?.inbetriebnahmen.find(l => l.id === id);

    if (id == null || !inb || this.listeNameSpeichert) {
      return;
    }

    const name = this.listeNameEntwurf.trim();

    if (!name) {
      this.listeNameFehler = 'Bitte einen Namen eingeben.';
      return;
    }

    // Nichts geändert: einfach schließen, kein Request.
    if (name === inb.name) {
      this.listeBearbeitenAbbrechen();
      return;
    }

    this.listeNameSpeichert = true;
    this.listeNameFehler = '';

    this.inbetriebnahmeService
      .umbenenneListe(id, name)
      .subscribe({

        next: aktualisiert => {

          this.listeNameSpeichert = false;
          this.ersetzeInbetriebnahme(aktualisiert);
          this.listeInBearbeitungId = null;
          this.listeNameEntwurf = '';
        },

        error: (fehler: HttpErrorResponse) => {

          this.listeNameSpeichert = false;

          console.error(
            'Liste konnte nicht umbenannt werden:',
            fehler
          );

          this.listeNameFehler =
            fehler?.status === 403
              ? 'Für das Umbenennen fehlt die Berechtigung.'
              : fehler?.error?.message
                ?? 'Der Name konnte nicht gespeichert werden. Bitte erneut versuchen.';
        }
      });
  }


  listeDuplizieren(
    inb: MessstellenListeSummary
  ): void {

    if (!this.projekt || !this.canEdit || this.istListeBeschaeftigt(inb.id)) {
      return;
    }

    const projektId = this.projekt.id;

    this.duplizierendeInbetriebnahmen.add(inb.id);

    this.inbetriebnahmeService
      .dupliziereListe(inb.id)
      .subscribe({

        next: kopie => {

          this.duplizierendeInbetriebnahmen.delete(inb.id);

          if (!this.projekt || this.projekt.id !== projektId) {
            return;
          }

          /*
           * Die Kopie direkt unter das Original hängen – ohne Neuladen der
           * Seite. Der Server sortiert gleich (Importzeitpunkt, dann Id),
           * ein späteres Neuladen ändert die Reihenfolge also nicht.
           */
          const listen = this.projekt.inbetriebnahmen;
          const position = listen.findIndex(l => l.id === inb.id);
          listen.splice(position < 0 ? listen.length : position + 1, 0, kopie);
          this.projekt.inbetriebnahmenCount = listen.length;

          // Titel der Kopie sofort zum Umbenennen anbieten.
          this.listeBearbeiten(kopie);

          this.snackBar.open(
            `„${inb.name}“ wurde dupliziert.`,
            'OK',
            {
              duration: 3000
            }
          );

          // Gesamtfortschritt und Anlagenteil-Gruppen still nachziehen.
          this.lade(projektId, true);
        },

        error: (fehler: HttpErrorResponse) => {

          this.duplizierendeInbetriebnahmen.delete(inb.id);

          console.error(
            'Liste konnte nicht dupliziert werden:',
            fehler
          );

          this.snackBar.open(
            fehler?.status === 403
              ? (fehler?.error?.message
                ?? 'Für das Duplizieren fehlt die Berechtigung.')
              : (fehler?.error?.message
                ?? 'Die Liste konnte nicht dupliziert werden. Bitte erneut versuchen.'),
            'OK',
            {
              duration: 6000
            }
          );
        }
      });
  }


  /** Eine Übersichtszeile durch den frischen Serverstand ersetzen. */
  private ersetzeInbetriebnahme(
    aktualisiert: MessstellenListeSummary
  ): void {

    if (!this.projekt) {
      return;
    }

    const listen = this.projekt.inbetriebnahmen;
    const position = listen.findIndex(l => l.id === aktualisiert.id);

    if (position >= 0) {
      listen[position] = aktualisiert;
    }
  }


  // ========================================================
  // PDF EXPORT
  // ========================================================

  exportiert = false;


  // ========================================================
  // SPS-LISTEN UND SPS-FORTSCHRITT (echte API)
  // ========================================================

  /** SPS-Zuordnungslisten und PLC-Tag-Listen des Projekts (GET api/sps/listen). */
  spsListen: SpsImportListe[] = [];

  spsListenFehler = '';

  /** SPS-Fortschritt aus dem Projekt-Detail – getrennt vom Messstellen-Fortschritt. */
  spsFortschritt: SpsFortschritt = leererSpsFortschritt();


  constructor(
    private service: ProjektService,
    private inbetriebnahmeService: InbetriebnahmeService,
    private customerService: CustomerService,
    private equipmentService: EquipmentService,
    private protokollService: IbnProtokollService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private ibnWizard: IbnWizardService,
    private authService: AuthService,
    private spsImportApi: SpsImportApiService,
    private spsListenLoeschen: SpsListenLoeschenService
  ) { }

  /**
   * Bearbeiten, Löschen und neue Inbetriebnahmen nur für Benutzer mit
   * Schreibrecht. Der Server prüft dasselbe – die Oberfläche bietet nur nicht
   * mehr an, was ohnehin abgelehnt würde.
   */
  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  get canManageProjektAccess(): boolean {
    return this.authService.isSuperAdmin();
  }


  // ========================================================
  // INIT
  // ========================================================

  ngOnInit(): void {

    const id = Number(
      this.route.snapshot.paramMap.get('id')
    );

    if (!id) {

      this.loadError = 'Ungültige Projekt-Id.';
      this.isLoading = false;

      return;
    }

    this.lade(id);

    // SPS-Listen des Projekts über die echte API – unabhängig vom Projekt-Detail.
    this.ladeSpsListen(id);
  }


  // ========================================================
  // PROJEKT LADEN
  // ========================================================

  /**
   * Projekt laden. Mit `still` bleibt die Seite stehen und nur die Daten
   * werden ausgetauscht – für Nachladen nach einer Aktion (z. B. Duplizieren),
   * bei dem die Liste nicht kurz verschwinden soll. Ein Fehler wird dann nur
   * protokolliert; die Seite zeigt weiter den letzten Stand.
   */
  private lade(id: number, still = false): void {

    if (!still) {
      this.isLoading = true;
      this.loadError = '';
    }

    this.service
      .getProjekt(id)
      .subscribe({

        next: projekt => {

          // Inzwischen zu einem anderen Projekt gewechselt: Antwort verwerfen.
          if (still && this.projekt && this.projekt.id !== id) {
            return;
          }

          projekt.inbetriebnahmen =
            projekt.inbetriebnahmen ?? [];

          this.projekt = projekt;

          // SPS-Fortschritt kommt mit dem Projekt-Detail (ProjektDetailDto.SpsFortschritt).
          this.spsFortschritt = projekt.spsFortschritt ?? leererSpsFortschritt();

          this.ohneVerbindung = false;
          this.isLoading = false;
        },

        error: (fehler: HttpErrorResponse) => {

          if (still) {
            console.error('Projekt konnte nicht nachgeladen werden:', fehler);
            return;
          }

          this.ohneVerbindung =
            fehler?.status === 0
            || fehler?.status === 408
            || (
              fehler?.status >= 502
              && fehler?.status <= 504
            );

          this.loadError =
            this.ohneVerbindung
              ? 'Keine Verbindung zum Server. Projektdaten stehen offline nicht zur Verfügung.'
              : 'Das Projekt konnte nicht geladen werden.';

          this.isLoading = false;
        }

      });
  }


  // ========================================================
  // OFFLINE AUSWEG
  // ========================================================

  zuOfflineInbetriebnahmen(): void {

    this.router.navigate([
      '/wartung/inbetriebnahme'
    ]);
  }


  // ========================================================
  // PROJEKT FORTSCHRITT
  // ========================================================

  get fortschrittProzent(): number {

    return (
      this.projekt?.fortschrittProzent
      ?? 0
    );
  }


  // ========================================================
  // ANLAGENTEILE
  // ========================================================

  readonly ringUmfang =
    2 * Math.PI * 19;


  get anlagenteilGruppen(): InbetriebnahmeAnlagenteile[] {

    return (
      this.projekt?.anlagenteileJeInbetriebnahme
      ?? []
    );
  }


  get messstellenOhneAnlagenteil(): number {

    return (
      this.projekt?.messstellenOhneAnlagenteil
      ?? 0
    );
  }


  /**
   * SPS-Fortschritt je Anlagenteil (ProjektDetailDto.SpsAnlagenteile) – eigene
   * Gruppe neben den Inbetriebnahmen, nie mit den Messstellen verrechnet.
   */
  get spsAnlagenteile(): SpsAnlagenteilUebersicht {

    return (
      this.projekt?.spsAnlagenteile
      ?? leereSpsAnlagenteilUebersicht()
    );
  }


  get zeigeAnlagenteilFortschritt(): boolean {

    return (
      this.anlagenteilGruppen.length > 0
      || this.spsAnlagenteile.anlagenteile.length > 0
    );
  }


  trackBySpsAnlagenteil(
    _index: number,
    anlagenteil: SpsAnlagenteilFortschritt
  ): number {

    return anlagenteil.anlagenteilId;
  }


  get messstellenOhneAnlagenteilAusserhalb(): number {

    const inGruppen =
      this.anlagenteilGruppen.reduce(
        (
          summe,
          gruppe
        ) =>
          summe
          +
          (
            gruppe.messstellenOhneAnlagenteil
            ?? 0
          ),
        0
      );

    return Math.max(
      0,
      this.messstellenOhneAnlagenteil - inGruppen
    );
  }


  ringOffset(
    prozent: number
  ): number {

    const anteil =
      Math.min(
        100,
        Math.max(0, prozent)
      )
      / 100;

    return (
      this.ringUmfang
      * (1 - anteil)
    );
  }


  // ========================================================
  // IBN FORTSCHRITT
  // ========================================================

  inbetriebnahmeProzent(
    inb: MessstellenListeSummary
  ): number {

    return (
      inb.fortschrittProzent
      ?? 0
    );
  }


  // ========================================================
  // PROJEKT STATUS
  // ========================================================

  projektIstNichtGestartet(): boolean {

    return (
      this.projekt?.messstellenGestartet
      ?? 0
    ) === 0;
  }


  projektIstFertig(): boolean {

    const gesamt =
      this.projekt?.messstellenGesamt
      ?? 0;

    const abgeschlossen =
      this.projekt?.messstellenAbgeschlossen
      ?? 0;

    return (
      gesamt > 0
      &&
      abgeschlossen >= gesamt
    );
  }


  projektIstOffen(): boolean {

    return (
      !this.projektIstNichtGestartet()
      &&
      !this.projektIstFertig()
    );
  }


  projektStatusText(): string {

    if (this.projektIstFertig()) {
      return 'Geprüft';
    }

    if (this.projektIstNichtGestartet()) {
      return 'Nicht gestartet';
    }

    return 'Offen';
  }


  // ========================================================
  // IBN STATUS
  // ========================================================

  istNichtGestartet(
    inb: MessstellenListeSummary
  ): boolean {

    return (
      inb.messstellenMitPruefpunkten
      ?? 0
    ) === 0;
  }


  istFertig(
    inb: MessstellenListeSummary
  ): boolean {

    const gesamt =
      inb.messstellenCount
      ?? 0;

    const abgeschlossen =
      inb.messstellenAbgeschlossen
      ?? 0;

    return (
      gesamt > 0
      &&
      abgeschlossen >= gesamt
    );
  }


  istOffen(
    inb: MessstellenListeSummary
  ): boolean {

    return (
      !this.istNichtGestartet(inb)
      &&
      !this.istFertig(inb)
    );
  }


  inbetriebnahmeStatusText(
    inb: MessstellenListeSummary
  ): string {

    if (this.istFertig(inb)) {
      return 'Geprüft';
    }

    if (this.istNichtGestartet(inb)) {
      return 'Nicht gestartet';
    }

    return 'Offen';
  }


  // ========================================================
  // NAVIGATION
  // ========================================================

  back(): void {

    this.router.navigate([
      '/projekte'
    ]);
  }


  oeffneInbetriebnahme(
    inb: MessstellenListeSummary
  ): void {

    this.router.navigate(
      [
        '/wartung/inbetriebnahme/liste',
        inb.id
      ],
      {
        queryParams: {
          vonProjekt: this.projekt?.id
        }
      }
    );
  }


  neuLaden(): void {

    if (this.projekt) {
      this.lade(this.projekt.id);
      this.ladeSpsListen(this.projekt.id);
    }
  }


  neueInbetriebnahme(): void {

    if (!this.projekt) {
      return;
    }

    this.router.navigate(
      [
        '/wartung/inbetriebnahme/import'
      ],
      {
        queryParams: {
          projektId: this.projekt.id,
          projektName: this.projekt.name
        }
      }
    );
  }


  // ========================================================
  // PROJEKTLISTEN UND SPS-PRÜFUNG (echte API)
  //
  // „Neue Liste hinzufügen“ fragt zuerst den Listentyp ab. Messstellen- und
  // Verbraucherliste gehen unverändert in den bestehenden KI-Import, die
  // beiden SPS-Typen in den SPS-Import. Der SPS-Fortschritt kommt aus dem
  // Projekt-Detail des Backends und wird NICHT mit dem Messstellen-
  // Fortschritt verrechnet.
  // ========================================================

  /** Alle Listen des Projekts: Inbetriebnahmen plus SPS-Listen. */
  get anzahlListen(): number {

    return (this.projekt?.inbetriebnahmen.length ?? 0)
      + this.spsListen.length;
  }


  /** Zustand der SPS-Prüfung in den Worten der bestehenden Status-Badges. */
  get spsStatusText(): string {

    const f = this.spsFortschritt;

    if (f.gesamt > 0 && f.bearbeitet >= f.gesamt) {
      return 'Geprüft';
    }

    if (f.bearbeitet === 0) {
      return 'Nicht gestartet';
    }

    return 'Offen';
  }


  /** SPS-Listen des Projekts über die echte API (GET api/sps/listen). */
  private ladeSpsListen(projektId: number): void {

    this.spsImportApi.getListen(projektId).subscribe({

      next: listen => {
        this.spsListen = listen;
        this.spsListenFehler = '';
      },

      error: () => {
        // Ohne SPS-Backend (z. B. Migration noch nicht angewendet) bleibt die
        // Projektansicht nutzbar; die SPS-Zeilen fehlen dann nur.
        this.spsListen = [];
        this.spsListenFehler = 'SPS-Listen konnten nicht geladen werden.';
      }
    });
  }


  /** Leere Inbetriebnahmeliste ohne Excel – eigene Struktur oder Vorlage. */
  neueLeereListe(): void {
    if (!this.projekt || !this.canEdit) { return; }
    this.router.navigate(['/wartung/inbetriebnahme/neu'], {
      queryParams: { projektId: this.projekt.id, projektName: this.projekt.name }
    });
  }


  /** Gemeinsamer Einstieg: Dateityp wird im Importassistenten erkannt. */
  zumMehrfachimport(): void {
    if (!this.projekt || !this.canEdit) { return; }
    this.router.navigate(['/projekte', this.projekt.id, 'import'], {
      queryParams: { projektName: this.projekt.name }
    });
  }

  neueListeHinzufuegen(): void {

    if (!this.projekt) {
      return;
    }

    const projekt = this.projekt;

    this.dialog
      .open(
        ListentypAuswahlDialogComponent,
        {
          data: {
            projektName: projekt.name
          },
          width: '600px',
          maxWidth: '95vw',
          autoFocus: false
        }
      )
      .afterClosed()
      .subscribe((typ: ProjektListenTyp | null | undefined) => {

        if (!typ) {
          return;
        }

        if (!istSpsListenTyp(typ)) {
          // Bestehender Ablauf – exakt wie bisher „Neue Inbetriebnahme“.
          this.neueInbetriebnahme();
          return;
        }

        this.router.navigate(
          [
            '/projekte',
            projekt.id,
            'import'
          ],
          {
            queryParams: {
              listenTyp: typ,
              projektName: projekt.name
            }
          }
        );
      });
  }


  oeffneSpsListe(
    liste: SpsImportListe
  ): void {

    if (!this.projekt) {
      return;
    }

    this.router.navigate([
      '/projekte',
      this.projekt.id,
      'sps-listen',
      liste.id
    ]);
  }


  zurSpsPruefung(): void {

    if (!this.projekt) {
      return;
    }

    this.router.navigate([
      '/projekte',
      this.projekt.id,
      'sps-pruefung'
    ]);
  }


  /** Listentyp einer echten Inbetriebnahme – Altbestände gelten als Messstellenliste. */
  listenTypInfoVon(
    inb: MessstellenListeSummary
  ): ListenTypInfo {

    return listenTypInfo(
      inb.listenTyp === 'Verbraucherliste'
        ? 'Verbraucherliste'
        : 'Messstellenliste'
    );
  }


  spsListenTypInfo(
    liste: SpsImportListe
  ): ListenTypInfo {

    return listenTypInfo(liste.listenTyp);
  }


  trackBySpsListe(
    _index: number,
    liste: SpsImportListe
  ): number {

    return liste.id;
  }


  /** Vollständige Übersicht aller SPS-Listen des Projekts (Öffnen, Löschen). */
  zurSpsListenUebersicht(): void {

    if (!this.projekt) {
      return;
    }

    this.router.navigate(
      ['/projekte', this.projekt.id, 'sps-listen'],
      { queryParams: { projektName: this.projekt.name } }
    );
  }


  /** Ids der SPS-Listen, deren Löschen gerade läuft (Ladezustand je Zeile). */
  private loeschendeSpsListen = new Set<number>();

  istSpsListeAmLoeschen(id: number): boolean {
    return this.loeschendeSpsListen.has(id);
  }

  /**
   * SPS-Liste löschen – derselbe Ablauf wie in der SPS-Listenübersicht
   * (Löschvorschau, Bestätigungsdialog, Backend, Snackbar). Gemeinsame
   * Signale mit weiteren Quellen bleiben samt Prüfung bestehen.
   */
  deleteSpsListe(
    liste: SpsImportListe,
    event?: Event
  ): void {

    event?.stopPropagation();

    if (!this.projekt || !this.canEdit || this.istSpsListeAmLoeschen(liste.id)) {
      return;
    }

    const projektId = this.projekt.id;
    this.loeschendeSpsListen.add(liste.id);

    this.spsListenLoeschen.loeschen(liste).subscribe(ergebnis => {

      this.loeschendeSpsListen.delete(liste.id);

      if (ergebnis) {
        this.spsListen = this.spsListen.filter(l => l.id !== liste.id);
        this.spsFortschritt = ergebnis.fortschritt;
        this.ladeSpsListen(projektId);
      }
    });
  }


  // ========================================================
  // INBETRIEBNAHME LÖSCHEN
  // ========================================================

  istInbetriebnahmeAmLoeschen(
    id: number
  ): boolean {

    return (
      this.loeschendeInbetriebnahmen.has(id)
    );
  }


  deleteListe(
    liste: MessstellenListeSummary,
    event?: Event
  ): void {

    /*
     * Die komplette Kachel ist klickbar.
     * Das Löschen darf deshalb NICHT zusätzlich die IBN öffnen. Aus dem
     * Drei-Punkte-Menü kommt kein Ereignis mehr mit – das Menü stoppt es
     * selbst (listeAktionenKlick).
     */
    event?.stopPropagation();

    // Eine laufende Umbenennung dieser Zeile verwerfen.
    if (this.listeInBearbeitungId === liste.id && !this.listeNameSpeichert) {
      this.listeBearbeitenAbbrechen();
    }


    if (
      !this.projekt
      ||
      this.istInbetriebnahmeAmLoeschen(liste.id)
    ) {
      return;
    }


    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      maxWidth: 'calc(100vw - 32px)',
      data: {
        title: 'Liste löschen?',
        message: `Möchtest du „${liste.name}“ wirklich löschen?`,
        details: [`${liste.messstellenCount} Messstellen sind betroffen.`],
        warning: 'Die Messstellen und zugehörigen Inbetriebnahmedaten dieser Liste werden gelöscht. Dieser Schritt kann nicht rückgängig gemacht werden.',
        confirmText: 'Liste löschen'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed || !this.projekt || this.istInbetriebnahmeAmLoeschen(liste.id)) {
        return;
      }

      const projektId = this.projekt.id;
      this.loeschendeInbetriebnahmen.add(liste.id);
    this.inbetriebnahmeService
      .deleteListe(liste.id)
      .subscribe({

        next: () => {

          this.loeschendeInbetriebnahmen.delete(
            liste.id
          );


          this.snackBar.open(
            'Inbetriebnahme gelöscht.',
            'OK',
            {
              duration: 3000
            }
          );


          /*
           * Nicht nur die IBN-Kachel lokal entfernen.
           *
           * Das Projekt wird komplett neu geladen, damit auch:
           *
           * - Gesamtfortschritt
           * - Messstellenanzahl
           * - Projektstatus
           * - Anlagenteil-Gruppen
           * - Fortschritt der Anlagenteile
           *
           * garantiert den aktuellen Serverstand zeigen.
           */
          this.lade(projektId);
        },


        error: err => {

          this.loeschendeInbetriebnahmen.delete(
            liste.id
          );


          console.error(
            'Inbetriebnahme konnte nicht gelöscht werden:',
            err
          );


          this.snackBar.open(
            err?.status === 403
              ? 'Für das Löschen der Inbetriebnahme fehlt die Berechtigung.'
              : (
                err?.error?.message
                ?? 'Die Inbetriebnahme konnte nicht gelöscht werden.'
              ),
            'OK',
            {
              duration: 4000
            }
          );
        }

      });
    });
  }

  // ========================================================
  // IBN-ASSISTENT
  // ========================================================

  /**
   * Startet den optionalen Coachmark-Assistenten. Er zeigt nur auf die
   * vorhandenen Bedienelemente – der Ablauf selbst bleibt unverändert.
   */
  ibnAssistentStarten(): void {

    if (!this.projekt) {
      return;
    }

    this.ibnWizard.starten();
  }


  // ========================================================
  // IBN PROTOKOLL PDF
  // ========================================================

  protokollExportieren(): void {

    if (
      !this.projekt
      ||
      this.exportiert
    ) {
      return;
    }


    this.dialog
      .open(
        IbnProtokollDialogComponent,
        {
          data: {
            projekt: this.projekt
          },
          width: '560px',
          maxWidth: '95vw',
          autoFocus: false
        }
      )
      .afterClosed()
      .subscribe(
        (
          auswahl:
            IbnProtokollAuswahl
            | null
            | undefined
        ) => {

          if (!auswahl) {
            return;
          }

          // Projekt und Inbetriebnahme sind mit dem Hauptdialog fertig
          // ausgewaehlt. Die Messstellen-Varianten brauchen einen zweiten
          // Dialog, in dem die Messstellen selbst gewaehlt werden.
          if (auswahl.umfang === 'messstelle') {
            this.oeffneMessstellenDialog(auswahl);
            return;
          }

          this.erzeugeProtokoll(auswahl);
        }
      );
  }


  /**
   * Zweiter Schritt der Messstellen-Varianten.
   *
   * 'einzeln' fuehrt zur bisherigen Einzelauswahl, 'combinedPdf' und 'zip'
   * teilen sich dieselbe Mehrfachauswahl - dort unterscheidet nur der Modus.
   */
  private oeffneMessstellenDialog(
    auswahl: IbnProtokollAuswahl
  ): void {

    if (!this.projekt) {
      return;
    }


    if (auswahl.variante === 'einzeln') {

      this.dialog
        .open(
          IbnProtokollMessstelleDialogComponent,
          {
            data: {
              projekt: this.projekt
            },
            width: '560px',
            maxWidth: '95vw',
            autoFocus: false
          }
        )
        .afterClosed()
        .subscribe(
          (
            einzelAuswahl:
              IbnProtokollAuswahl
              | null
              | undefined
          ) => {

            if (einzelAuswahl) {
              this.erzeugeProtokoll(einzelAuswahl);
            }
          }
        );

      return;
    }


    const modus = auswahl.variante === 'zip' ? 'zip' : 'combinedPdf';

    this.dialog
      .open(
        IbnProtokollMessstellenDialogComponent,
        {
          data: {
            projekt: this.projekt,
            modus
          },
          width: '760px',
          maxWidth: '95vw',
          autoFocus: false
        }
      )
      .afterClosed()
      .subscribe(
        (
          mehrfachAuswahl:
            IbnProtokollMessstellenAuswahl
            | null
            | undefined
        ) => {

          if (mehrfachAuswahl) {
            this.erzeugeMessstellenProtokolle(mehrfachAuswahl);
          }
        }
      );
  }


  private erzeugeProtokoll(
    auswahl: IbnProtokollAuswahl
  ): void {

    this.exportiert = true;


    this.snackBar.open(
      'Das IBN-Protokoll wird erstellt …',
      undefined,
      {
        duration: 2000
      }
    );


    this.ladeProtokoll(auswahl)
      .subscribe({

        next: protokoll => {

          downloadIbnProtokollPdf(
            protokoll
          )
            .then(() => {

              this.exportiert = false;

              this.snackBar.open(
                `IBN-Protokoll für „${auswahl.bezeichnung}" erstellt.`,
                'OK',
                {
                  duration: 3000
                }
              );
            })
            .catch(() => {

              this.exportiert = false;

              this.snackBar.open(
                'Das PDF konnte nicht erzeugt werden.',
                'OK',
                {
                  duration: 4000
                }
              );
            });
        },


        error: err => {

          this.exportiert = false;

          this.snackBar.open(
            err?.status === 404
              ? 'Für den gewählten Umfang sind keine Daten (mehr) vorhanden.'
              : 'Die Daten für das Protokoll konnten nicht geladen werden.',
            'OK',
            {
              duration: 4000
            }
          );
        }

      });
  }


  private ladeProtokoll(
    auswahl: IbnProtokollAuswahl
  ): Observable<IbnProtokoll> {

    if (
      auswahl.umfang === 'messstelle'
      &&
      auswahl.messstelleId != null
    ) {

      return this.protokollService
        .getMessstelleProtokoll(
          auswahl.messstelleId
        );
    }


    if (
      auswahl.umfang === 'inbetriebnahme'
      &&
      auswahl.inbetriebnahmeId != null
    ) {

      return this.protokollService
        .getInbetriebnahmeProtokoll(
          auswahl.inbetriebnahmeId
        );
    }


    return this.protokollService
      .getProjektProtokoll(
        auswahl.projektId
      );
  }


  // ========================================================
  // IBN PROTOKOLL: MEHRERE MESSSTELLEN
  // ========================================================

  /**
   * Sammel-PDF oder ZIP ueber mehrere Messstellen.
   *
   * Beide Varianten holen dieselben Daten wie der Einzelexport - je Messstelle
   * ein Aufruf von /ibnprotokoll/messstelle/{id}. Unterschiedlich ist nur,
   * was daraus gebaut wird.
   */
  private erzeugeMessstellenProtokolle(
    auswahl: IbnProtokollMessstellenAuswahl
  ): void {

    if (
      this.exportiert
      ||
      auswahl.messstellen.length === 0
    ) {
      return;
    }


    this.exportiert = true;

    const anzahl = auswahl.messstellen.length;

    this.snackBar.open(
      `${anzahl} Messstelle${anzahl === 1 ? '' : 'n'} werden aufbereitet …`,
      undefined,
      {
        duration: 2500
      }
    );


    this.ladeMessstellenProtokolle(auswahl.messstellen)
      .subscribe({

        next: protokolle => {

          const fertig = auswahl.modus === 'zip'
            ? this.erzeugeMessstellenZip(auswahl, protokolle)
            : downloadIbnProtokollSammelPdf({
              titel: auswahl.inbetriebnahmeName,
              dateiName: this.sammelDateiName(auswahl),
              protokolle
            });

          fertig
            .then(() => {

              this.exportiert = false;

              this.snackBar.open(
                auswahl.modus === 'zip'
                  ? `ZIP mit ${anzahl} IBN-Protokoll${anzahl === 1 ? '' : 'en'} erstellt.`
                  : `IBN-Protokoll mit ${anzahl} Messstelle${anzahl === 1 ? '' : 'n'} erstellt.`,
                'OK',
                {
                  duration: 3000
                }
              );
            })
            .catch(() => {

              this.exportiert = false;

              this.snackBar.open(
                auswahl.modus === 'zip'
                  ? 'Die ZIP-Datei konnte nicht erzeugt werden.'
                  : 'Das PDF konnte nicht erzeugt werden.',
                'OK',
                {
                  duration: 4000
                }
              );
            });
        },


        error: err => {

          this.exportiert = false;

          this.snackBar.open(
            err?.status === 404
              ? 'Zu mindestens einer gewaehlten Messstelle sind keine Daten (mehr) vorhanden.'
              : 'Die Daten fuer das Protokoll konnten nicht geladen werden.',
            'OK',
            {
              duration: 4000
            }
          );
        }

      });
  }


  /**
   * Laedt die Einzelprotokolle der gewaehlten Messstellen.
   *
   * Bewusst nur wenige Anfragen gleichzeitig: bei einer Inbetriebnahme mit
   * mehreren hundert Messstellen wuerde ein forkJoin ueber alle den Server
   * in einem Schwung treffen. Die Reihenfolge der Exportliste wird
   * anschliessend wiederhergestellt.
   */
  private ladeMessstellenProtokolle(
    messstellen: MessstelleAuswahlOption[]
  ): Observable<IbnProtokoll[]> {

    const GLEICHZEITIG = 4;

    return from(messstellen)
      .pipe(

        mergeMap(
          (messstelle, position) =>
            this.protokollService
              .getMessstelleProtokoll(messstelle.id)
              .pipe(
                map(protokoll => ({ position, protokoll }))
              ),
          GLEICHZEITIG
        ),

        toArray(),

        map(
          eintraege =>
            eintraege
              .sort((a, b) => a.position - b.position)
              .map(e => e.protokoll)
        )
      );
  }


  /** Ein eigenes PDF je Messstelle, gebuendelt in einer ZIP-Datei. */
  private async erzeugeMessstellenZip(
    auswahl: IbnProtokollMessstellenAuswahl,
    protokolle: IbnProtokoll[]
  ): Promise<void> {

    const eintraege: ZipEintrag[] = [];

    // Nacheinander, nicht parallel: jedes PDF liegt vollstaendig im Speicher,
    // und die Erzeugung ist ohnehin an einen Thread gebunden.
    for (let i = 0; i < protokolle.length; i++) {

      const protokoll = protokolle[i];
      const messstelle = auswahl.messstellen[i];

      // Genau dasselbe Layout wie beim Einzelexport.
      const daten = await buildIbnProtokollPdfBytes(protokoll);

      const name = messstelle
        ? auswahlAnzeigeName(messstelle)
        : protokoll.titel;

      eintraege.push({
        name: `${sichererDateiName(name, `Messstelle_${i + 1}`)}_IBN_Protokoll.pdf`,
        daten
      });
    }

    downloadZip(
      `IBN_Protokolle_${sichererDateiName(auswahl.inbetriebnahmeName, 'Inbetriebnahme')}.zip`,
      eintraege
    );
  }


  /** Dateiname des Sammel-PDFs, ohne Endung. */
  private sammelDateiName(
    auswahl: IbnProtokollMessstellenAuswahl
  ): string {

    return `IBN_Protokolle_${sichererDateiName(auswahl.inbetriebnahmeName, 'Inbetriebnahme')}`;
  }


  // ========================================================
  // PROJEKT BEARBEITEN
  // ========================================================

  bearbeiten(): void {

    if (!this.projekt) {
      return;
    }


    this.form = {
      name: this.projekt.name,
      projektNummer: this.projekt.projektNummer,
      customerId:
        this.projekt.customerId
        ?? null,
      equipmentId:
        this.projekt.equipmentId
        ?? null,
      userIds: []
    };


    this.formError = '';
    this.editOpen = true;


    if (this.kunden.length === 0) {

      this.customerService
        .getCustomers()
        .subscribe({

          next: kunden => {

            this.kunden =
              kunden ?? [];
          },

          error: () => {

            this.kunden = [];
          }

        });
    }


    if (this.projekt.customerId) {

      this.ladeAnlagen(
        this.projekt.customerId
      );
    }
  }

  zugriffVerwalten(): void {

    if (
      !this.projekt
      ||
      !this.canManageProjektAccess
    ) {
      return;
    }


    this.zugriffOpen = true;

    this.loadingZugriff = true;

    this.zugriffError = '';

    this.ausgewaehlteUserIds = [];


    /*
     * Zuerst alle auswählbaren Benutzer laden.
     */
    this.service
      .getZugriffsBenutzer()
      .subscribe({

        next: benutzer => {

          this.zugriffsBenutzer =
            benutzer ?? [];


          /*
           * Danach aktuelle Projektzuweisungen laden.
           */
          this.service
            .getProjektZugriffe(
              this.projekt!.id
            )
            .subscribe({

              next: userIds => {

                this.ausgewaehlteUserIds =
                  userIds ?? [];

                this.loadingZugriff =
                  false;
              },


              error: () => {

                this.loadingZugriff =
                  false;

                this.zugriffError =
                  'Die aktuellen Projektzugriffe konnten nicht geladen werden.';
              }

            });
        },


        error: () => {

          this.loadingZugriff =
            false;

          this.zugriffError =
            'Die Benutzer konnten nicht geladen werden.';
        }

      });
  }

  zugriffAbbrechen(): void {

    this.zugriffOpen = false;

    this.zugriffError = '';

    this.zugriffsBenutzer = [];

    this.ausgewaehlteUserIds = [];
  }

  zugriffSpeichern(): void {

    if (
      !this.projekt
      ||
      !this.canManageProjektAccess
      ||
      this.savingZugriff
    ) {
      return;
    }


    this.savingZugriff = true;

    this.zugriffError = '';


    this.service
      .setProjektZugriffe(
        this.projekt.id,
        this.ausgewaehlteUserIds
      )
      .subscribe({

        next: () => {

          this.savingZugriff =
            false;

          this.zugriffOpen =
            false;


          this.snackBar.open(
            'Projektzugriff gespeichert.',
            'OK',
            {
              duration: 2500
            }
          );
        },


        error: err => {

          this.savingZugriff =
            false;

          this.zugriffError =
            err?.error?.message
            ?? err?.error
            ?? 'Der Projektzugriff konnte nicht gespeichert werden.';
        }

      });
  }


  abbrechen(): void {

    this.editOpen = false;
    this.formError = '';
  }


  onKundeChange(
    customerId: number | null
  ): void {

    this.form.customerId =
      customerId ?? null;


    this.form.equipmentId = null;

    this.anlagen = [];


    if (customerId) {

      this.ladeAnlagen(
        customerId
      );
    }
  }


  private ladeAnlagen(
    customerId: number
  ): void {

    this.loadingAnlagen = true;


    this.equipmentService
      .getEquipmentsByCustomer(
        customerId
      )
      .subscribe({

        next: anlagen => {

          this.anlagen =
            anlagen ?? [];

          this.loadingAnlagen = false;
        },


        error: () => {

          this.anlagen = [];

          this.loadingAnlagen = false;
        }

      });
  }

  get ausgewaehlteBenutzer(): ProjektZugriffBenutzer[] {

    return this.zugriffsBenutzer.filter(
      user =>
        this.ausgewaehlteUserIds.includes(
          user.id
        )
    );
  }

  zugriffBenutzerEntfernen(
    userId: string
  ): void {

    this.ausgewaehlteUserIds =
      this.ausgewaehlteUserIds.filter(
        id => id !== userId
      );
  }


  get istGueltig(): boolean {

    return (
      this.form.name.trim().length > 0
      &&
      this.form.projektNummer.trim().length > 0
    );
  }


  speichern(): void {

    if (
      !this.projekt
      ||
      this.saving
      ||
      !this.istGueltig
    ) {
      return;
    }


    this.saving = true;
    this.formError = '';


    const request: ProjektRequest = {

      name:
        this.form.name.trim(),

      projektNummer:
        this.form.projektNummer.trim(),

      customerId:
        this.form.customerId,

      equipmentId:
        this.form.equipmentId
        ?? null,

      userIds: []
    };


    this.service
      .updateProjekt(
        this.projekt.id,
        request
      )
      .subscribe({

        next: aktualisiert => {

          aktualisiert.inbetriebnahmen =
            aktualisiert.inbetriebnahmen
            ?? [];


          this.projekt =
            aktualisiert;


          this.saving = false;

          this.editOpen = false;


          this.snackBar.open(
            'Projekt gespeichert.',
            'OK',
            {
              duration: 2500
            }
          );
        },


        error: err => {

          this.saving = false;


          this.formError =
            err?.error?.message
            ??
            (
              err?.status === 403
                ? 'Für das Ändern von Projekten fehlt die Berechtigung.'
                : 'Das Projekt konnte nicht gespeichert werden.'
            );
        }

      });
  }


  // ========================================================
  // PROJEKT LÖSCHEN
  // ========================================================

  loeschen(): void {

    if (!this.projekt) {
      return;
    }


    const anzahl =
      this.projekt.inbetriebnahmen.length;


    const zusatz =
      anzahl > 0
        ? (
          `\n\nDie ${anzahl} zugeordnete`
          + `${anzahl === 1 ? '' : 'n'} `
          + `Inbetriebnahme`
          + `${anzahl === 1 ? '' : 'n'} `
          + `bleib`
          + `${anzahl === 1 ? 't' : 'en'} `
          + `erhalten und verlier`
          + `${anzahl === 1 ? 't' : 'en'} `
          + `nur die Projektzuordnung.`
        )
        : '';


    if (
      !confirm(
        `Projekt "${this.projekt.name}" `
        + `(${this.projekt.projektNummer}) `
        + `wirklich löschen?`
        + zusatz
      )
    ) {

      return;
    }


    this.service
      .deleteProjekt(
        this.projekt.id
      )
      .subscribe({

        next: () => {

          this.snackBar.open(
            'Projekt gelöscht.',
            'OK',
            {
              duration: 2500
            }
          );


          this.router.navigate([
            '/projekte'
          ]);
        },


        error: err => {

          this.snackBar.open(
            err?.status === 403
              ? 'Für das Löschen von Projekten fehlt die Berechtigung.'
              : 'Das Projekt konnte nicht gelöscht werden.',
            'OK',
            {
              duration: 4000
            }
          );
        }

      });
  }


  // ========================================================
  // TRACK BY
  // ========================================================

  trackByInbetriebnahme(
    _index: number,
    inb: MessstellenListeSummary
  ): number {

    return inb.id;
  }


  trackByGruppe(
    _index: number,
    gruppe: InbetriebnahmeAnlagenteile
  ): number {

    return gruppe.inbetriebnahmeId;
  }


  trackByAnlagenteil(
    index: number,
    anlagenteil: AnlagenteilFortschritt
  ): string {

    return `${index}-${anlagenteil.id}`;
  }
}